/**
 * Client-side service preview — mirrors backend garageServiceExpansion.util.js
 */
import {
  normalizeGarageTier,
  tierMeetsFloor,
  getClusterById,
  resolveFuelSpecialistTypes,
} from '../constants/garageServiceClusters';
import { CLUSTER_SERVICES_BY_TIER } from '../constants/garageClusterServiceData';

export function expandClusterServices(tier, clusterId, options = {}) {
  const canonical = normalizeGarageTier(tier) || 'small';
  const id = Number(clusterId);
  const cluster = getClusterById(id);
  if (!cluster || !tierMeetsFloor(canonical, cluster.tierFloor)) return [];

  const byTier = CLUSTER_SERVICES_BY_TIER[id];
  if (!byTier) return [];

  let services = [];
  if (canonical === 'large' && byTier.large) {
    services = [...(byTier.small || []), ...(byTier.medium || []), ...byTier.large];
  } else if (canonical === 'medium' && byTier.medium) {
    services = [...(byTier.small || []), ...byTier.medium];
  } else if (byTier.small) {
    services = [...byTier.small];
  }

  if (id === 10) {
    const fuelTypes = Array.isArray(options.fuelSpecialistTypes) ? options.fuelSpecialistTypes : [];
    const hasCng = fuelTypes.includes('cng_lpg');
    const hasEv = fuelTypes.includes('ev_hybrid');
    if (fuelTypes.length > 0) {
      services = services.filter((name) => {
        const lower = name.toLowerCase();
        if (lower.includes('cng') || lower.includes('lpg')) return hasCng;
        if (lower.includes('ev') || lower.includes('hybrid')) {
          return hasEv && canonical === 'large';
        }
        return true;
      });
    }
  }

  const seen = new Set();
  return services.filter((s) => {
    const key = s.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function previewGarageServices({ companyType, selectedClusters, fuelSpecialistTypes = [] }) {
  const tier = normalizeGarageTier(companyType);
  const clusters = (Array.isArray(selectedClusters) ? selectedClusters : [])
    .map(Number)
    .filter((n) => n >= 1 && n <= 10);
  if (!tier || !clusters.length) return [];

  const resolvedFuel = resolveFuelSpecialistTypes(tier, clusters, fuelSpecialistTypes);
  const seen = new Set();
  const out = [];
  for (const clusterId of clusters) {
    for (const name of expandClusterServices(tier, clusterId, { fuelSpecialistTypes: resolvedFuel })) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(name);
    }
  }
  return out.sort((a, b) => a.localeCompare(b));
}

export function previewGarageServicesByCluster({
  companyType,
  selectedClusters,
  fuelSpecialistTypes = [],
}) {
  const tier = normalizeGarageTier(companyType);
  const clusterIds = (Array.isArray(selectedClusters) ? selectedClusters : [])
    .map(Number)
    .filter((n) => n >= 1 && n <= 10)
    .sort((a, b) => a - b);
  if (!tier || !clusterIds.length) return [];

  const resolvedFuel = resolveFuelSpecialistTypes(tier, clusterIds, fuelSpecialistTypes);
  const groups = [];
  for (const clusterId of clusterIds) {
    const cluster = getClusterById(clusterId);
    if (!cluster) continue;
    const services = expandClusterServices(tier, clusterId, { fuelSpecialistTypes: resolvedFuel });
    if (services.length) {
      groups.push({
        clusterId,
        label: cluster.label,
        services,
      });
    }
  }
  return groups;
}
